/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  setDrawerSize,
  setHistory,
  setNavigationSize,
} from '../../store/global';
import { saveAll } from '../../store/items';
import { isEditable } from '../../utils/roles';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { BoardGrid } from '../board/board.Grid';
import { LinkAll } from '../link/link.All';
import { PicAll } from '../pic/pic.All';
import { TextAll } from '../text/text.All';
import { ItemHeader } from './item.Header';
import { ItemToolBox } from './item.ToolBox';
import { ItemMobileNavigation } from './item.MobileNavigation';
import { getId, isTabCampaignAccessible, canEditItemForItem } from '../../utils/item';
import { useBoardReadOnlyMode } from '../../context/boardReadOnly';

export const ItemShort = props => {
  const {
    displayType,
    itemId,
    refContainer,
    height,
    disableSelfFetch = false,
    disableTransitions,
    drawerIsWide,
    sidebarOpenOverride,
    sidebarTabOverride,
    onSidebarClose,
  } = props;

  const shortType = useSelector(state => state.global[displayType]);
  const isMobile = useSelector(state => state.global.isMobile);
  const item = useSelector(state => state.items[itemId]);
  const allItems = useSelector(state => state.items);
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const drawerSize = useSelector(state => state.global.drawer.size);
  const navigationSize = useSelector(state => state.global.navigation.size);
  const dispatch = useDispatch();
  const inheritedBoardReadOnlyMode = useBoardReadOnlyMode();

  const currentMobileTab = useSelector(state => state.global.currentMobileTab);
  const [mobileTab, setMobileTab] = useState(null);
  const lastDrawerLogRef = useRef(null);
  const inflightFetchesRef = useRef(new Set());
  const fetchedOnceRef = useRef(new Set());
  const effectiveIsMobile =
    displayType === 'drawer' && typeof drawerIsWide === 'boolean'
      ? !drawerIsWide
      : isMobile;
  const effectiveSidebarOpen =
    displayType === 'sidebar' && typeof sidebarOpenOverride === 'boolean'
      ? sidebarOpenOverride
      : shortType?.open;

  const isValidObjectId = id => /^[a-f0-9]{24}$/i.test(id);

  const tabIds = useMemo(() => {
    if (!item?.tabs) {
      return [];
    }
    return item.tabs
      .map(tabObj => getId(tabObj))
      .filter(Boolean);
  }, [item?.tabs]);

  const itemsById = useSelector(state => {
    const mapped = {};
    if (itemId && state.items[itemId]) {
      mapped[itemId] = state.items[itemId];
    }
    tabIds.forEach(tabId => {
      const tab = state.items[tabId];
      if (tab) {
        mapped[tabId] = tab;
        const campaignId = tab?.campaign;
        const worldId = tab?.world;
        const universeId = tab?.universe;
        if (campaignId && state.items[campaignId]) {
          mapped[campaignId] = state.items[campaignId];
        }
        if (worldId && state.items[worldId]) {
          mapped[worldId] = state.items[worldId];
        }
        if (universeId && state.items[universeId]) {
          mapped[universeId] = state.items[universeId];
        }
      }
    });
    return mapped;
  }, shallowEqual);

  const getItems = async (id, reason) => {
    if (!id || !isValidObjectId(id)) {
      return;
    }
    if (inflightFetchesRef.current.has(id)) {
      return;
    }
    if (fetchedOnceRef.current.has(id)) {
      const existing = allItems?.[id];
      if (existing && existing.__partial !== true) {
        return;
      }
    }
    inflightFetchesRef.current.add(id);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/items/${id}`);
      if (res.data) {
        dispatch(saveAll({ item: res.data }));
      }
      fetchedOnceRef.current.add(id);
    } catch (error) {
      // Ignore transient 404s when an item was just deleted.
      if (error?.response?.status !== 404) {
        console.error('Failed to fetch item:', id, error);
      }
    } finally {
      inflightFetchesRef.current.delete(id);
    }
  };

  useEffect(() => {
    if (displayType !== 'drawer' || !itemId) {
      return;
    }
    if (lastDrawerLogRef.current === itemId) {
      return;
    }
    lastDrawerLogRef.current = itemId;
  }, [displayType, itemId]);

  // GET ITEM IF IS NOT CURRENT AND IS NOT IN STORE
  useEffect(() => {
    if (disableSelfFetch) {
      return;
    }
    if (itemId && (displayType === 'drawer' || displayType === 'tooltip')) {
      if (
        (!item || itemId !== item._id || item?.__partial) &&
        (!itemsById[itemId] || itemsById[itemId].__partial)
      ) {
        getItems(itemId, 'drawer-or-tooltip-missing-or-partial');
      }
      dispatch(setHistory(itemId));
    }
  }, [itemId, displayType, disableSelfFetch]);

  // ENSURE ITEM IS LOADED FOR SIDEBAR/CARD RENDERING
  useEffect(() => {
    if (disableSelfFetch) {
      return;
    }
    if (!itemId) {
      return;
    }
    const existing = allItems?.[itemId] ?? itemsById[itemId];
    if (!existing || existing.__partial) {
      getItems(itemId, 'ensure-loaded');
    }
  }, [itemId, itemsById, allItems, disableSelfFetch]);

  // PREFETCH GRID TABS FOR DRAWER TO AVOID EMPTY SIDEBAR
  useEffect(() => {
    if (disableSelfFetch) {
      return;
    }
    if (
      (displayType !== 'drawer' && displayType !== 'tooltip') ||
      !item?.tabs
    ) {
      return;
    }
    tabIds.forEach(tabId => {
      if (!itemsById[tabId] && isValidObjectId(tabId)) {
        getItems(tabId, 'prefetch-tab');
      }
    });
  }, [displayType, tabIds, itemsById, item?.tabs, disableSelfFetch]);

  const gridTabIds = useMemo(() => {
    if (!item?.tabs) {
      return [];
    }
    return item.tabs
      .map(tabObj => {
        const tabId = getId(tabObj);
        const display = tabObj?.[tabId]?.display;
        if (display && display !== 'grid') {
          return null;
        }
        return tabId;
      })
      .filter(Boolean);
  }, [item?.tabs]);

  const firstAccessibleGridTabId = gridTabIds?.find(tabId => {
    const tab = itemsById?.[tabId];
    if (!tab) {
      return false;
    }
    return isTabCampaignAccessible(tab, itemsById, listCampaignsId);
  });
  const hasExplicitSidebarSetting = Boolean(item) &&
    Object.prototype.hasOwnProperty.call(item, 'sidebar');
  const prefersDefaultSidebarDisplay =
    hasExplicitSidebarSetting && item?.sidebar === null;

  // PICK CURRENT OR FIRST ACCESSIBLE TAB FOR MOBILE SIDEBAR
  useEffect(() => {
    if (!effectiveIsMobile || !item?.tabs || prefersDefaultSidebarDisplay) {
      if (mobileTab !== null) {
        setMobileTab(null);
      }
      return;
    }
    const accessibleGridTabIds = (gridTabIds || []).filter(tabId => {
      const tab = itemsById?.[tabId];
      if (!tab) {
        return false;
      }
      return isTabCampaignAccessible(tab, itemsById, listCampaignsId);
    });

    // On mobile, we want the sidebar tab to be the default shown tab.
    // `currentMobileTab[itemId]` still wins if it points to an accessible tab.
    const sidebarTab = itemsById?.[item?.sidebar];
    const sidebarIsAccessible =
      !sidebarTab ||
      isTabCampaignAccessible(sidebarTab, itemsById, listCampaignsId);
    const accessibleTabIds = new Set(accessibleGridTabIds);
    if (item?.sidebar && sidebarIsAccessible) {
      accessibleTabIds.add(item.sidebar);
    }
    const defaultTabId =
      (item?.sidebar && sidebarIsAccessible ? item.sidebar : null) ??
      accessibleGridTabIds[0];

    const currentTab = currentMobileTab?.[itemId];
    const nextTab =
      currentTab && accessibleTabIds.has(currentTab) ? currentTab : defaultTabId;
    const nextValue = nextTab ?? null;
    if (nextValue !== mobileTab) {
      setMobileTab(nextValue);
    }
  }, [
    currentMobileTab?.[itemId],
    effectiveIsMobile,
    item?.tabs,
    itemsById,
    itemId,
    listCampaignsId,
    mobileTab,
    prefersDefaultSidebarDisplay,
  ]);

  const sidebarTabId = (() => {
    if (sidebarTabOverride && sidebarTabOverride !== 'sidebar') {
      const overrideTab = itemsById?.[sidebarTabOverride];
      if (!overrideTab) {
        return sidebarTabOverride;
      }
      if (isTabCampaignAccessible(overrideTab, itemsById, listCampaignsId)) {
        return sidebarTabOverride;
      }
      return null;
    }
    if (prefersDefaultSidebarDisplay) {
      return null;
    }
    const preferSidebarTab =
      displayType === 'drawer' && drawerIsWide === false;
    const candidate = preferSidebarTab
      ? item?.sidebar || mobileTab || firstAccessibleGridTabId
      : effectiveIsMobile
      ? mobileTab || item?.sidebar || firstAccessibleGridTabId
      : item?.sidebar || mobileTab || firstAccessibleGridTabId;
    if (!candidate) {
      return null;
    }
    const tab = itemsById?.[candidate];
    if (!tab) {
      return candidate;
    }
    if (isTabCampaignAccessible(tab, itemsById, listCampaignsId)) {
      return candidate;
    }
    return firstAccessibleGridTabId ?? null;
  })();
  const sidebarTab = sidebarTabId ? itemsById?.[sidebarTabId] : null;
  const canMj =
    isMjForItem(item, allItems) || canUseMjForItem(item, allItems);
  const canEdit = canEditItemForItem(item, allItems);
  const sidebarBoardReadOnlyMode =
    inheritedBoardReadOnlyMode ||
    (!canMj ? sidebarTab?.readOnlyMode || null : null);
  const canEditSidebarBoardWidgets =
    displayType !== 'tooltip' &&
    isEditable() &&
    canEdit &&
    sidebarBoardReadOnlyMode !== 'hard';
  const canEditSidebarBoardLayout =
    displayType !== 'tooltip' &&
    isEditable() &&
    canEdit &&
    !sidebarBoardReadOnlyMode;

  // GET ITEM TAB (current sidebar board) IF IS NOT IN STORE
  useEffect(() => {
    if (disableSelfFetch) {
      return;
    }
    if (!sidebarTabId) {
      return;
    }
    const existing = allItems?.[sidebarTabId] ?? itemsById[sidebarTabId];
    if (!existing && isValidObjectId(sidebarTabId)) {
      getItems(sidebarTabId, 'sidebar-tab');
    }
  }, [sidebarTabId, itemsById, allItems, disableSelfFetch]);

  useEffect(() => {
    if (disableSelfFetch) {
      return;
    }
    if (!item) {
      return;
    }
    const scopedCampaign = item?.campaign ? allItems?.[item.campaign] : null;
    const scopeIds = Array.from(
      new Set([
        item?.campaign,
        item?.world,
        item?.universe,
        scopedCampaign?.universe,
      ]),
    ).filter(Boolean);
    scopeIds.forEach(scopeId => {
      const existing = allItems?.[scopeId];
      if ((!existing || existing.__partial) && isValidObjectId(scopeId)) {
        getItems(scopeId, 'permission-scope');
      }
    });
  }, [
    allItems,
    disableSelfFetch,
    item,
  ]);

  useEffect(() => {
    if (isMobile === true && shortType?.size) {
      if (drawerSize !== shortType?.size) {
        dispatch(setDrawerSize(shortType?.size));
      }
      if (navigationSize !== shortType?.size) {
        dispatch(setNavigationSize(shortType?.size));
      }
    }
  }, [shortType?.size, isMobile, drawerSize, navigationSize]);

  if (item?._id === undefined)
    return (
      <div
        className={`${
          displayType === 'card'
            ? ''
            : 'bg-main border-r border-main-d border-opacity-20'
        } flex flex-col gap-1 ${
          disableTransitions ? 'transition-none' : 'transition-all'
        } overflow-hidden`}
        style={{
          width: shortType?.size,
          height:
            height === 'auto'
              ? 'auto'
              : displayType === 'card'
              ? refContainer?.current?.offsetHeight
              : '100dvh',
          marginLeft:
            displayType === 'sidebar'
              ? effectiveSidebarOpen === false
                ? -shortType?.size
                : 0
              : 0,
        }}
      >
        loading
      </div>
    );
  else {
    return (
      <div
        className={`${
          displayType === 'card'
            ? ''
            : 'bg-main border-r border-main-d border-opacity-20'
        } flex flex-col gap-1 ${
          disableTransitions ? 'transition-none' : 'transition-all'
        } overflow-hidden`}
        style={{
          width: shortType?.size,
          height:
            height === 'auto'
              ? 'auto'
              : displayType === 'card'
              ? refContainer?.current?.offsetHeight
              : '100dvh',
          marginLeft:
            displayType === 'sidebar'
              ? effectiveSidebarOpen === false
                ? -shortType?.size
                : 0
              : 0,
        }}
      >
        <div
          className='text-contrast overflow-y-auto overflow-x-hidden'
          style={{
            height:
              height === 'auto' ? 'auto' : height ?? 'calc(100dvh - 46px)',
          }}
        >
          {displayType !== 'card' && (
            <ItemHeader
              displayType={displayType}
              itemId={item?._id}
            />
          )}
          {sidebarTabId && item.type !== 'pic' ? (
            <BoardGrid
              itemId={item._id}
              boardId={sidebarTabId}
              boardWidth={
                refContainer?.current?.offsetWidth ?? shortType?.size ?? 300
              }
              editable={canEditSidebarBoardWidgets}
              layoutEditable={canEditSidebarBoardLayout}
            />
          ) : (
            <div>
              <PicAll
                itemId={itemId}
                hideFirsts={true}
                zoom={6}
              />
              {item.type !== 'pic' && <TextAll itemId={itemId} />}
              <LinkAll itemId={itemId} />
            </div>
          )}
        </div>

        {item &&
          displayType !== 'card' &&
          isEditable() &&
          canEdit &&
          (displayType === 'sidebar' || !sidebarBoardReadOnlyMode) &&
          isMobile === false && (
            <ItemToolBox
              itemId={item?._id}
              displayType={displayType}
              currentBoard={sidebarTabId}
              hasBoard={sidebarTabId}
              onSidebarClose={onSidebarClose}
            />
          )}
        {item &&
          displayType !== 'card' &&
          isEditable() &&
          isMobile === true && (
            <ItemMobileNavigation
              itemId={item?._id}
              displayType={displayType}
              currentBoard={mobileTab ?? sidebarTabId}
              hasBoard={sidebarTabId}
            />
          )}
      </div>
    );
  }
};
